/**
 * HVOR ET VARSEL TAR DEG — én regel for klokka, varselfanen og oversikten (QA5-B).
 *
 * Klokka hadde sin egen liste med sju typer; oversikten og varselfanen hadde en annen med tre. En
 * invitasjon til et møte eller en ny advance var derfor klikkbar i klokka og død tekst de to andre
 * stedene, og hendelsen lenket til en kalenderparameter ingen leste. Nå er det én funksjon, og en
 * type som ikke står her er tekst overalt.
 */
import type { NotificationDto, OrganizationKind } from '@tvitvi/shared';

import { enquiryDestination } from '@/features/inbox/thread-object-link';

export interface NotificationRoute {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
}

/**
 * Ruten et varsel peker på, eller null.
 *
 * `objectType` og `objectId` er null sammen eller satt sammen — `ck_notifications__object_pairing`
 * håndhever det — så en rad enten navigerer et sted eller er ren tekst.
 */
export function notificationRoute(
  row: Pick<NotificationDto, 'objectType' | 'objectId'> & {
    type?: string;
    i18nParams?: Record<string, string>;
  },
  kind: OrganizationKind,
): NotificationRoute | null {
  const { objectType, objectId } = row;
  /* F21: artisten er satt på et reisefølge i byråets hus — «Mine konserter» i huset hun leser i. */
  if (row.type === 'booking_party.member_added') return { to: '/concerts' };
  /* Eksporten er klar eller feilet (docs/EKSPORT.md §8): «Mine eksporter», der fila står. */
  if (row.type === 'export.ready' || row.type === 'export.failed') return { to: '/exports' };
  /*
   * 257.11: kontaktforespørselen er et kort i en samtale i Innboks (15, runde D). Samtalen lages
   * når Innboks åpnes, i organisasjonen man står i, og står da øverst og ulest — så varselet går
   * til Innboks (varselet skrives før samtalen finnes, og har ingen tråd å peke på).
   */
  if (row.type === 'person_connection.requested') return { to: '/inbox' };
  if (objectType === null || objectId === null) return null;
  /*
   * OPPGAVEN ER GITT FRA ET ANNET HUS (sak 216.9, 0395). Den står ikke i huset som leser, så
   * /tasks/:id ville vært 404 — «Mine oppgaver» har den, med husets navn.
   */
  if (row.type === 'task.given') return { to: '/tasks' };
  /* 0417 (agent E): regionbeskyttelsen er brutt — avtalefanen, der vilkåret og advarslene står. */
  if (row.type === 'concert.region_conflict' && objectType === 'concert') {
    return {
      to: '/shows/$concertId',
      params: { concertId: objectId },
      search: { tab: 'agreements' },
    };
  }

  /*
   * Agent C: en datoforespørsel i husets hus åpner Datoforespørsler for spillestedet (beholderen).
   * F-4 (Q 29.09): og selve forespørselen, når varselet vet hvilken (`enquiryId`, se lytteren) —
   * ellers landet en melding på en bekreftet forespørsel på en tom «Venter på oss».
   */
  if (row.type?.startsWith('venue_enquiry.') === true && objectType === 'festival') {
    const enquiry = row.i18nParams?.['enquiryId'];
    return {
      to: '/venue/$festivalId/requests',
      params: { festivalId: objectId },
      ...(enquiry === undefined || enquiry === '' ? {} : { search: { enquiry } }),
    };
  }

  /*
   * F5 (runde 4. oktober): en lenkeperson åpnet eller bekreftet. Lenken har ingen tråd, så varselet
   * peker på årgangen — personlista der viser lenkestatusen. Et spillesteds personer bor et annet sted.
   */
  if ((row.type === 'share.opened' || row.type === 'share.confirmed') && objectType === 'edition') {
    const festivalId = row.i18nParams?.['festivalId'];
    if (festivalId === undefined || festivalId === '') return null;
    return row.i18nParams?.['festivalKind'] === 'venue'
      ? { to: '/venue/$festivalId/people', params: { festivalId } }
      : {
          to: '/festivals/$festivalId/editions/$editionId/people',
          params: { festivalId, editionId: objectId },
        };
  }

  switch (objectType) {
    case 'task':
      return { to: '/tasks/$taskId', params: { taskId: objectId } };
    case 'thread':
      return { to: '/inbox/$threadId', params: { threadId: objectId } };
    case 'contact':
      return { to: '/contacts/$contactId', params: { contactId: objectId } };
    /*
     * De to bookingtypene deler side og skiller seg på søkeparameteren, fordi de to husene har
     * hver sin rad for den samme samtalen (avgjørelse 185). Den samme delingen som `targetFor` i
     * innboksen gjør — se `thread-object-link.ts`.
     */
    case 'booking_enquiry':
      return enquiryDestination(objectId, kind);
    case 'booking_bid':
      return kind === 'agency' ? null : { to: '/booking', search: { bid: objectId } };
    /* Kalenderen åpner hendelsen i panelet sitt fra `?event=` — se `CalendarWorkspace`. */
    case 'event':
      return { to: '/calendar', search: { event: objectId } };
    /* 201.14: «du er satt på en vakt» lander der svaret gis. */
    case 'shift':
      return { to: '/my-shifts' };
    /* Konserten hos arrangøren (spillested): salen er endret, regionbeskyttelsen brutt … */
    case 'concert':
      return { to: '/shows/$concertId', params: { concertId: objectId } };
    /*
     * Kontrakten (1035): de to partene har hver sin side for den samme kontrakten, så varselet går via
     * `/contracts/$contractId`, som slår opp siden for huset som leser og åpner skuffen der.
     */
    case 'contract':
      return { to: '/contracts/$contractId', params: { contractId: objectId } };
    /* 284.2 (1145): artistens Personer-fane, der byrået endrer eller fjerner tilgangen. */
    case 'booking_artist':
      return { to: '/artists/$artistId', params: { artistId: objectId }, search: { tab: 'people' } };
    default:
      return null;
  }
}
